import React, { useMemo, useRef, useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import {
  FilterBar, DateRangePicker, ExportButton, KpiCard, ChartCard, DataTable,
  CategoryBar,
} from '../../components/analytics';
import { formatTRY, formatPct, formatCompact } from '../../utils/analytics/format';
import { exportPdf } from '../../utils/analytics/export';
import { useSearchParams } from 'react-router-dom';
import { rangeFromUrl } from '../../utils/analytics/dateRange';
import { useAnalyticsQuery } from '../../hooks/useAnalyticsQuery';
import { useFirmalar } from '../../hooks/useFirmalar';
import ErrorBanner from '../../components/ErrorBanner';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const tryFetch = async (url, signal) => {
  try {
    const res = await fetch(url, { signal, headers: authHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};

export default function StokUretimAnalizPage({ embedded = false }) {
  const { theme } = useTheme();
  // Cockpit drill-down dönem bağlamı (bu sayfa firma yerine lokasyon bazlı)
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState(() => rangeFromUrl(searchParams));
  const [lokasyonId, setLokasyonId] = useState('');
  // SO-05: firma kapsamı (cockpit drill-down firma bağlamını URL'den taşır)
  const [firma, setFirma] = useState(() => searchParams.get('firma') || '');
  const containerRef = useRef(null);

  const { data: firmalar } = useFirmalar();

  const stokParams = useMemo(() => {
    const p = new URLSearchParams();
    if (lokasyonId) p.set('lokasyon_id', lokasyonId);
    if (firma) p.set('firma', firma);
    return p.toString();
  }, [lokasyonId, firma]);

  const { data: stokOzet, loading: loadingS, error, refresh: refreshS } = useAnalyticsQuery(
    `stok-ozet:${stokParams}`,
    async ({ signal }) => {
      const res = await fetch(
          `${API}/api/analytics/stok/ozet?${stokParams}${stokParams ? '&' : ''}bitis=${range.end}`,
          { signal, headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`Hata ${res.status}`);
      return res.json();
    },
    [stokParams, range.end],
  );

  const { data: lokasyonlar } = useAnalyticsQuery(
    'lokasyonlar',
    ({ signal }) => tryFetch(`${API}/api/lokasyonlar`, signal),
    [],
  );

  const { data: parcalamaEvents, loading: loadingP } = useAnalyticsQuery(
    `disassembly-${range.start}-${range.end}`,
    ({ signal }) => tryFetch(`${API}/api/disassembly/events?baslangic=${range.start}&bitis=${range.end}&limit=1000`, signal),
    [range],
  );

  // Negatif stok reconcile analizi (sistem geneli; lokasyon/firma filtresinden bağımsız worklist):
  // TL değer + anomali (değer-yoğun → elle denetim) + aktif(süren drift) + sınıf + aylık drift.
  const { data: negAnaliz, loading: loadingNA } = useAnalyticsQuery(
    'negatif-stok-analiz',
    ({ signal }) => tryFetch(`${API}/api/stok/negatif-stok/analiz`, signal),
    [],
  );

  const negDriftMetni = useMemo(() => {
    const d = negAnaliz?.drift_aylik || [];
    if (!d.length) return '';
    return d.map((m) => `${m.ay}: ${m.net >= 0 ? '+' : ''}${formatCompact(m.net)}`).join(' · ');
  }, [negAnaliz]);

  const parcalamaRandiman = useMemo(() => {
    const events = Array.isArray(parcalamaEvents) ? parcalamaEvents : (parcalamaEvents?.items || []);
    if (events.length === 0) return null;
    const rands = events.map(e => e.randiman_pct ?? e.randiman ?? null).filter(v => v != null && !Number.isNaN(Number(v)));
    if (rands.length === 0) return null;
    return rands.reduce((s, v) => s + Number(v), 0) / rands.length;
  }, [parcalamaEvents]);

  const kritikTop10 = useMemo(() => {
    const items = stokOzet?.kritik_urunler || [];
    return items.slice(0, 10).map(u => ({
      label: `${u.kod || ''} ${(u.ad || '').slice(0, 25)}`.trim(),
      value: Number(u.eksik || 0),
    }));
  }, [stokOzet]);

  const onPdf = () => {
    if (containerRef.current) exportPdf(containerRef.current, `stok-uretim-${range.start}_${range.end}`);
  };

  const loading = loadingS;

  const Wrapper = ({ children }) =>
    embedded ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space.lg, fontFamily: theme.font.family }}>
        {children}
      </div>
    ) : (
      <div ref={containerRef} style={{
        padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg,
        background: theme.color.surfaceAlt, minHeight: '100vh', fontFamily: theme.font.family,
      }}>{children}</div>
    );

  const selectStyle = {
    padding: `${theme.space.xs}px ${theme.space.md}px`,
    borderRadius: theme.radius.sm, border: `1px solid ${theme.color.border}`,
    background: theme.color.surface, color: theme.color.text,
    fontSize: theme.font.size.xs, fontFamily: theme.font.family,
  };

  return (
    <Wrapper>
      {!embedded && (
        <header>
          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
            Stok & Üretim Analizi
          </h1>
          <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
            Stok değeri, kritik ürünler ve parçalama randımanı
          </div>
        </header>
      )}

      <FilterBar sticky={!embedded}>
        <DateRangePicker value={range} onChange={setRange} />
        <select value={firma} onChange={(e) => setFirma(e.target.value)} style={selectStyle}>
          <option value="">Tüm Firmalar</option>
          {(Array.isArray(firmalar) ? firmalar : []).map(f => (
            <option key={f.kod} value={f.kod}>{f.ad}</option>
          ))}
        </select>
        <select value={lokasyonId} onChange={(e) => setLokasyonId(e.target.value)} style={selectStyle}>
          <option value="">Tüm Lokasyonlar</option>
          {(Array.isArray(lokasyonlar) ? lokasyonlar : []).map(l => (
            <option key={l.id} value={l.id}>{l.ad}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <ExportButton onExportPdf={onPdf} />
        </div>
      </FilterBar>

      {error && <ErrorBanner error={String(error?.message || error)} onRetry={refreshS} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.space.base }}>
        <KpiCard
          title="Net Stok Değeri"
          value={loading ? undefined : formatTRY(stokOzet?.toplam_deger || 0)}
          tooltip="Net pozisyon = pozitif + negatif stok değeri. Negatif (fazla çıkış) pozisyonlar düşülür."
          loading={loading}
        />
        <KpiCard title="Pozitif Stok Değeri" value={loading ? undefined : formatTRY(stokOzet?.pozitif_deger || 0)} loading={loading} />
        <KpiCard
          title="Negatif Pozisyon Değeri"
          value={loading ? undefined : formatTRY(stokOzet?.negatif_deger || 0)}
          tooltip="Negatif stoklu (fazla çıkış / veri hatası) satırların değer toplamı — operasyonel inceleme gerektirir."
          increaseIsBad
          loading={loading}
        />
        <KpiCard title="Kritik Ürün" value={loading ? undefined : String(stokOzet?.kritik_urun_sayisi || 0)} increaseIsBad loading={loading} />
        <KpiCard
          title="Stokta Yok"
          value={loading ? undefined : String(stokOzet?.stokta_yok_sayisi || 0)}
          tooltip="Min stok tanımsız olduğu halde stok ≤ 0 olan kalemler (gizli stokout). Min stok tanımlanmalı."
          increaseIsBad
          loading={loading}
        />
        <KpiCard title="Negatif Stok" value={loading ? undefined : String(stokOzet?.negatif_stok_sayisi || 0)} increaseIsBad loading={loading} />
        <KpiCard
          title="Farklı Malzeme"
          value={loading ? undefined : String(stokOzet?.farkli_malzeme_sayisi || 0)}
          tooltip={`Benzersiz malzeme sayısı. Toplam ${stokOzet?.toplam_kalem_sayisi ?? 0} stok kaydı (lokasyon bazlı).`}
          loading={loading}
        />
        <KpiCard
          title="Parçalama Randımanı"
          value={parcalamaRandiman == null ? null : formatPct(parcalamaRandiman)}
          emptyLabel="Dönemde parçalama yok"
          loading={loadingP}
        />
      </div>

      <ChartCard title="Top 10 Kritik Ürün" subtitle="En fazla eksik miktar" height={320}
                 loading={loading} empty={!loading && kritikTop10.length === 0}>
        <CategoryBar data={kritikTop10} formatter={formatCompact} />
      </ChartCard>

      <ChartCard title="Kritik Ürün Listesi" loading={loading}
                 empty={!loading && (!stokOzet?.kritik_urunler || stokOzet.kritik_urunler.length === 0)}>
        <DataTable
          rows={(stokOzet?.kritik_urunler || []).map((u, i) => ({ id: u.malzeme_id ?? i, ...u }))}
          columns={[
            { key: 'kod', label: 'Kod' },
            { key: 'ad', label: 'Ad' },
            { key: 'lokasyon_ad', label: 'Lokasyon' },
            { key: 'mevcut', label: 'Mevcut', align: 'right', render: (v) => Number(v).toFixed(1) },
            { key: 'min_stok', label: 'Min', align: 'right', render: (v) => Number(v).toFixed(1) },
            {
              key: 'eksik', label: 'Eksik', align: 'right',
              render: (v) => <span style={{ color: v > 50 ? theme.color.danger : theme.color.text }}>{Number(v).toFixed(1)}</span>
            },
          ]}
          onRowClick={() => window.location.assign('/stok/durum')}
          initialSort={{ key: 'eksik', dir: 'desc' }}
        />
      </ChartCard>

      <ChartCard title="Negatif Pozisyonlar" subtitle="Fazla çıkış / veri hatası — operasyonel inceleme gerektirir" loading={loading}
                 empty={!loading && (!stokOzet?.negatif_urunler || stokOzet.negatif_urunler.length === 0)}>
        <DataTable
          rows={(stokOzet?.negatif_urunler || []).map((u, i) => ({ id: u.malzeme_id ?? i, ...u }))}
          columns={[
            { key: 'kod', label: 'Kod' },
            { key: 'ad', label: 'Ad' },
            { key: 'lokasyon_ad', label: 'Lokasyon' },
            { key: 'miktar', label: 'Miktar', align: 'right', render: (v) => <span style={{ color: theme.color.danger }}>{Number(v).toFixed(1)}</span> },
            { key: 'birim_fiyat', label: 'Birim Fiyat', align: 'right', render: (v) => formatTRY(Number(v || 0)) },
            { key: 'deger', label: 'Değer', align: 'right', render: (v) => <span style={{ color: theme.color.danger }}>{formatTRY(Number(v || 0))}</span> },
          ]}
          onRowClick={() => window.location.assign('/stok/durum')}
          initialSort={{ key: 'deger', dir: 'asc' }}
        />
      </ChartCard>

      <ChartCard
        title="Negatif Stok — Reconcile Analizi"
        subtitle={
          `Sistem geneli worklist · ${negAnaliz?.ozet?.anomali_adet || 0} anomali (değer-yoğun → elle denetim) · `
          + `${negAnaliz?.ozet?.aktif_adet || 0} aktif (son 30g hareket = süren drift) · `
          + `HIC_IKMAL ${negAnaliz?.ozet?.HIC_IKMAL?.adet || 0} / AZ_IKMAL ${negAnaliz?.ozet?.AZ_IKMAL?.adet || 0}`
          + (negDriftMetni ? ` · Şube drift: ${negDriftMetni}` : '')
        }
        loading={loadingNA}
        empty={!loadingNA && (!negAnaliz?.kayitlar || negAnaliz.kayitlar.length === 0)}
      >
        <DataTable
          rows={(negAnaliz?.kayitlar || []).map((k, i) => ({ id: `${k.lokasyon_id}-${k.malzeme_id}-${i}`, ...k }))}
          columns={[
            { key: 'malzeme_kod', label: 'Kod' },
            { key: 'malzeme_ad', label: 'Ad' },
            { key: 'lokasyon_ad', label: 'Lokasyon' },
            { key: 'guncel_stok', label: 'Stok', align: 'right', render: (v) => <span style={{ color: theme.color.danger }}>{Number(v).toFixed(1)}</span> },
            { key: 'deger', label: 'Değer', align: 'right', render: (v) => <span style={{ color: theme.color.danger }}>{formatTRY(Number(v || 0))}</span> },
            { key: 'sinif', label: 'Sınıf' },
            {
              key: 'aktif_30g', label: 'Durum',
              render: (v) => v
                ? <span style={{ color: theme.color.danger, fontSize: 12 }}>● süren</span>
                : <span style={{ color: theme.color.textSoft, fontSize: 12 }}>○ tarihsel</span>
            },
            {
              key: 'anomali', label: 'Anomali', align: 'center',
              render: (v) => v ? <span title="Değer-yoğun/aşırı miktar — körlemesine reconcile etme, ham veri denetle" style={{ color: theme.color.danger }}>⚠️</span> : ''
            },
          ]}
          onRowClick={() => window.location.assign('/stok/durum')}
          initialSort={{ key: 'deger', dir: 'asc' }}
        />
      </ChartCard>

      <ChartCard title="Stokta Yok (min stok tanımsız)" subtitle="Stok ≤ 0 ama min stok girilmemiş — gizli stokout, master data tamamlanmalı" loading={loading}
                 empty={!loading && (!stokOzet?.stokta_yok_urunler || stokOzet.stokta_yok_urunler.length === 0)}>
        <DataTable
          rows={(stokOzet?.stokta_yok_urunler || []).map((u, i) => ({ id: u.malzeme_id ?? i, ...u }))}
          columns={[
            { key: 'kod', label: 'Kod' },
            { key: 'ad', label: 'Ad' },
            { key: 'lokasyon_ad', label: 'Lokasyon' },
            { key: 'mevcut', label: 'Mevcut', align: 'right', render: (v) => Number(v).toFixed(1) },
          ]}
          onRowClick={() => window.location.assign('/stok/durum')}
          initialSort={{ key: 'mevcut', dir: 'asc' }}
        />
      </ChartCard>
    </Wrapper>
  );
}
